// eslint-disable-next-line no-unused-vars
import React, { useMemo } from 'react';
import { bool, array, func, object, number } from 'prop-types';
import {
  TextArea,
  SkeletonText,
  RadioButton,
  TextInputSkeleton,
  Checkbox,
} from '@carbon/react';
import { Info, CircleAlert } from 'lucide-react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { uniqueId, noop } from 'lodash';
import memoize from 'micro-memoize';
import { dequal } from 'dequal';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import { NWComboBox, NWTextInput, NWRadioButtonGroup } from 'Components/shared';
import { COUNTRIES } from 'Constants';
import usePermissions from 'Hooks/usePermissions';
import { sortItemsParenthesisLast, filterItemsByName } from 'Utils/comboBoxUtils';

const StyledContainer = styled.div`
  width: 100%;
  display: grid;
  grid-gap: 2.25rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: -2rem 0 0;

  /* .dsOrderType fieldset {
    margin: 5px -10px;
    flex-direction: row;

    .cds--radio-button-wrapper {
      border: 0;
      width: auto;
      padding: 0;
      margin: 0 10px;
    }
  } */

  .dsOrderType, .dsOrderMode {
    .cds--radio-button-group {
      flex-direction: column;
      width: 100%;
    }

    .radioHidden {
      display: none !important;
    }

    legend.cds--label {
      margin: 0;
    }
  }

  .dsOrderTypeRef {
    display: flex;
    align-items: center;
    gap: 8px;

    .internalRef, .deliveryLockerCode {
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 1;

      .cds--tooltip__label {
        margin-bottom: 12px;
      }

      .cds--tooltip__trigger {
        margin: 0;
      }
    }
  }

  
  .transportOptions {
    [name='transportOptions'] {
      display: flex;
      align-items: center;
      width: 100%;
      border-bottom: 1px solid ${ props => props.theme.products.colors.border };

      .cds--radio-button-wrapper {
        border: 0;
      }

      .cds--radio-button__label svg {
        margin-left: 8px;
      }
    }

    .cds--radio-button-group {
      flex-direction: column;
      width: 100%;
    }
  }

  .paymentOptions {
    margin-top: 0;
  }

  section {
    border: 1px solid ${ props => props.theme.products.colors.border };
    margin: 2rem 0;

    .titleContainer {
      display: flex;
      align-items: center;
      height: 60px;
      font-size: 1.25rem;
      color: ${ props => props.theme.colors.darkGray };
      background-color: ${ props => props.theme.products.colors.addToFavorite };
      text-align: left;
      padding: 0 14px;

      span {
        display: block;
        min-width: 30px;
        min-height: 30px;
        margin-right: 10px;
        border-radius: 30px;
        background-color: #041B54;
        color: ${ props => props.theme.colors.white };
        font-size: 1.25rem;
        text-align: center;
        line-height: 30px;
      }
    }

    .body {
      padding: 20px 14px;
        //border: 1px solid ${ props => props.theme.products.colors.addToFavoriteBorder };

      .addressesSkeleton {
        .cds--label {
          margin: 0;
        }
      }

      p {
        color: ${ props => props.theme.colors.darkGray };
        font-size: 0.875rem;
        word-break: break-all;

        span {
          font-weight: 600;
          margin-right: 5px;
        }
      }

      .cds--skeleton {
        margin: 10px 0;
      }

      .cod {
        margin-top: 5px;
        display: flex;
        align-items: center;

        svg {
          margin-right: 5px;
        }
      }
    }

    input.hidden {
      opacity: 0;
      height: 1px;
      width: 1px;
      margin: 0;
      border: 0;
      padding: 0;
      position: relative;
      left: 0;
    }

    .checkboxWrapper {
      display: flex;
      align-items: center;

      .cds--form-item {
        width: auto;
        max-width: fit-content;
      }

      label {
        font-size: 0.875rem;
        color: ${ props => props.theme.colors.darkGray };
        align-items: end;
      }

      .tooltipContainer {
        margin-left: 8px;
      }
    }

    .deliveryConditions {
      background-color: ${ props => props.theme.colors.altGray };
      padding: 20px 15px;
      margin: 30px -14px -20px;

      .title {
        font-weight: 600;
        font-size: 1rem;
        color: ${ props => props.theme.colors.darkGray };
        margin-bottom: 8px;
      }

      div, p {
        font-size: 0.8125rem;
        line-height: 1rem;
        color: ${ props => props.theme.colors.darkGray };
      }

      .warning {
        margin-top: 15px;
        color: ${ props => props.theme.colors.altOrange };
      }
    }

    .orderNotes {
      background-color: ${ props => props.theme.colors.white };
      outline: 0;
      border: 0;
      width: auto;

      .cds--text-area__wrapper--cols {
        width: 100%;
        
        .cds--text-area {
          width: 100%;
          background-color: transparent;
        }
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    grid-template-columns: auto;
    grid-gap: 0;
    margin-top: 0;

    .dsOrderTypeRef {
      flex-direction: column;
      align-items: start;

      > div {
        width: 100%;
      }
    }

    /* .dsOrderType fieldset {
      flex-direction: column;
      align-items: start;

      .cds--radio-button-wrapper {
          margin: 10px;
      }
    } */

    section {
      margin: 0;
      margin-top: 30px;
    }
  }
`;

const StyledTextInputSkeleton = styled.div`
  .cds--label {
    &:before {
      background: transparent;
      transform: none;
      transition: none;
      animation: none;
      will-change: unset;
    }

    &:after {
      content: '*';
      display: inline-block;
      margin-left: 5px;
      color: ${ props => props.theme.colors.default };
    }

    background: transparent;
    display: inline-block;
    font-size: 0.875rem;
    line-height: 1rem;
    color: ${ props => props.theme.colors.darkGray };
    width: auto;
    margin: 0 !important;
  }

  margin: 1px 0 15px;
`;

const OrderInfo = props => {
  const {
    setCity,
    setCounty,
    setCountry,
    setLocker,
    setShipping,
    setBilling,
    setPaymentOption,
    setTransportOption,
    onChangeNote,
    loadingOptions,
    loadingAddresses,
    selectedDSCountry,
    handleOnChangeDeliveryType,
    handleOnChangeDeliveryMode,
    mappedDeliveryTypes,
  } = props;
  const { dropShipment = false } = props?.order || {};
  // const { formatNumber } = useNumberUtils();
  const theme = useTheme();
  const { t } = useTranslation();
  const { isExternalReseller } = usePermissions();

  const getKeyByValue = value => {
    const key = Object.keys(mappedDeliveryTypes).find(item => mappedDeliveryTypes[item] === value);
    return key || null; // return key if found, otherwise return null
  };

  const sortedCities = useMemo(() => sortItemsParenthesisLast(props?.cities), [props?.cities]);

  const getMemoizedDropShipmentBlocks = memoize(data => {
    const {
      order,
      editable,
      cities,
      counties,
      countries,
      lockers,
      transportOptions,
      paymentOptions,
      billingAddresses,
      selectedBillingAddress,
      canSelectedDSCountryPlaceEmagOrder,
      hasInternalDropShipmentSamedayLockerTransport,
      hasExternalDropShipmentSamedayLockerTransport,
      canPlaceEmagOrder,
      canPlaceExternalOrder,
      canPlaceInternalOrder,
      dsFormValidation,
      updateDsValidation,
      handleOnChangeName,
      handleOnChangeAddress,
      handleOnChangePhone,
      handleOnChangePostalCode,
      handleOnChangeInternalRefCode,
      handleOnDeliveryLockerCode,
      handleOnChangeBillingDetails,
      handleOnChangeRecipientEmail,
    } = data;

    if (!editable) {
      return (
        <>
          <div>
            <section>
              <div className="titleContainer">
                <span>1</span>
                <p dangerouslySetInnerHTML={{ __html: t('order.dsOrderInfo') }} />
              </div>
              <div className="body">
                <p>
                  <span>{ t('order.dropShippingData.name') }:</span>
                  {
                    order.deliveryAddressData?.name
                    || order.deliveryAddressData?.contactPersonName
                  }
                </p>
                <p>
                  <span>{ t('order.dropShippingData.country') }:</span>
                  {
                    order.deliveryAddressData?.country
                    /* eslint-disable-next-line eqeqeq */
                    || (countries || []).find(country => country.id == order.deliveryAddressData?.countryId)?.name
                  }
                </p>
                <p>
                  <span>{ t('order.dropShippingData.county') }:</span>
                  {
                    order.deliveryAddressData?.county
                    /* eslint-disable-next-line eqeqeq */
                    || (counties || []).find(county => county.erpCountyId == order.deliveryAddressData?.countyId)?.name
                  }
                </p>
                <p>
                  <span>{ t('order.dropShippingData.city') }:</span>
                  { (
                    typeof order.deliveryAddressData?.city === 'string'
                    && order.deliveryAddressData?.city?.length
                  )
                    ? order.deliveryAddressData?.city
                    : (cities || []).find(county => county.id === order.deliveryAddressData?.city)?.name || order.deliveryAddressData?.locality
                  }
                </p>
                <p>
                  <span>{ t('order.dropShippingData.address') }:</span>
                  { order.deliveryAddressData?.address || order.deliveryAddressData?.street }
                </p>
                <p>
                  <span>{ t('order.dropShippingData.phone') }:</span>
                  {
                    order.deliveryAddressData?.phone
                    || order.deliveryAddressData?.contactPersonPhone
                  }
                </p>
                { (order.deliveryAddressData?.email || order.deliveryAddressData?.contactPersonRecipientEmail) && (
                  <p>
                    <span>{ t('order.dropShippingData.recipientEmail') }:</span>
                    {
                      order.deliveryAddressData?.email
                      || order.deliveryAddressData?.contactPersonRecipientEmail
                    }
                  </p>
                ) }
                <p>
                  <span>{ t('order.dropShippingData.postalCode') }:</span>
                  { order.deliveryAddressData?.postalCode }
                </p>
                { order?.dropShipmentType && (
                  <p>
                    <span>{ t('order.deliveryType') }:</span>
                    { t(`order.dropShipmentType.${ getKeyByValue(order.dropShipmentType) }`) }
                  </p>
                ) }
                { order.locker && (
                  <p>
                    <span>{ t('order.dropShippingData.locker') }:</span>
                    { order.locker.name }
                  </p>
                ) }
                {
                  order?.clientOrderIdRef
                    ? (
                      <p className="ref">
                        <span>
                          { order?.deliveryAddressData?.deliveryType === 'emktp'
                            ? t('order.dropShippingData.emagOrder')
                            : t('order.dropShippingData.internalRef')
                          }
                          :
                        </span>
                        { order.clientOrderIdRef }
                      </p>
                    )
                    : null
                }
                {
                  order?.cashOnDelivery
                    ? (
                      <p className="cod">
                        <CircleAlert />{ t('order.awbSolicited') }
                      </p>
                    )
                    : null
                }
              </div>
            </section>
            <section>
              <div className="titleContainer">
                <span>2</span> <p>{ t('order.shippingMethod') }</p>
              </div>
              <div className="body">
                <p>
                  { (transportOptions || []).find(transportOption => transportOption.id === order.transportOption)?.name }
                </p>
              </div>
            </section>
          </div>
          <div>
            <section>
              <div className="titleContainer">
                <span>3</span> <p>{ t('order.billingDetails') }</p>
              </div>
              <div className="body">
                <p>
                  <span>{ t('order.billingAddress') }:</span>
                  { selectedBillingAddress?.label }
                </p>
                  { order.billingDetails && (
                    <p>
                      <span>{ t('order.billingDetails') }:</span>
                      { order.billingDetails }
                    </p>
                  ) }
              </div>
            </section>
            <section>
              <div className="titleContainer">
                <span>4</span> <p>{ t('order.paymentMethod') }</p>
              </div>
              <div className="body">
                <p>
                  { (paymentOptions || []).find(opt => opt.id === order.paymentOption)?.name }
                </p>
              </div>
            </section>
            { (order.orderNotes?.length > 0 || order.comment?.length > 0) && (
              <section className="orderNotes">
                <div className="body">
                  <p>{ order.orderNotes || order.comment }</p>
                </div>
              </section>
            ) }
          </div>
        </>
      );
    }

    return (
      <>
        <section>
          <div className="titleContainer">
            <span>1</span> <p dangerouslySetInnerHTML={{ __html: t('order.dsOrderInfo') }} />
          </div>
          <div className="body">
            <NWRadioButtonGroup
              theme={ theme }
              name="dsOrderType"
              className="dsOrderType"
              invalid={ !dsFormValidation.deliveryType.isSelected }
              invalidText={ t('errors.required') }
              onChange={ handleOnChangeDeliveryType }
              valueSelected={ order?.deliveryAddressData?.deliveryType }
              legendText={ t('order.dropShippingData.dsOrderType') }
            >
              <RadioButton
                id="internalCourier"
                className={`internalCourier ${!canPlaceInternalOrder ? 'radioHidden' : ''}`}
                labelText={ t('order.dropShippingData.internalCourier') }
                value="internalCourier"
                disabled={ !canPlaceInternalOrder }
              />
              <RadioButton
                id="externalCourier"
                className={`externalCourier ${!canPlaceExternalOrder ? 'radioHidden' : ''}`}
                labelText={ t('order.dropShippingData.externalCourier') }
                value="externalCourier"
                disabled={ !canPlaceExternalOrder }
              />
              <RadioButton
                id="emktp"
                className={`emktp ${!canSelectedDSCountryPlaceEmagOrder ? 'radioHidden' : ''}`}
                labelText={ t('order.dropShippingData.emagOrder') }
                value="emktp"
                disabled={ !canSelectedDSCountryPlaceEmagOrder || !canPlaceEmagOrder }
              />
            </NWRadioButtonGroup>
            <NWRadioButtonGroup
              theme={ theme }
              name="dsOrderMode"
              className="dsOrderMode"
              invalid={ !dsFormValidation.deliveryMode.isSelected }
              invalidText={ t('errors.required') }
              onChange={ handleOnChangeDeliveryMode }
              valueSelected={ order.deliveryAddressData?.deliveryMode }
              legendText={ t('order.dropShippingData.dsOrderMode') }
            >
              <RadioButton
                id="toAddress"
                labelText={ t('order.dropShippingData.toAddress') }
                value="address"
              />
              <RadioButton
                id="toLocker"
                labelText={ t('order.dropShippingData.toLocker') }
                disabled={
                  !order.deliveryAddressData?.deliveryType
                  || (order.deliveryAddressData?.deliveryType === 'internalCourier' && !hasInternalDropShipmentSamedayLockerTransport)
                  || (order.deliveryAddressData?.deliveryType === 'externalCourier' && !hasExternalDropShipmentSamedayLockerTransport)
                }
                value="locker"
              />
            </NWRadioButtonGroup>
            <NWTextInput
              id="name"
              name="name"
              autoComplete="none"
              light
              required
              onFocus={ () => updateDsValidation('name', { isTouched: true }) }
              invalid={ !dsFormValidation.name.isValid && dsFormValidation.name.isTouched }
              invalidText={ dsFormValidation.name.error }
              labelText={ t('order.dropShippingData.name') }
              onChange={ e => handleOnChangeName(e.target.value) }
              value={ order.deliveryAddressData?.contactPersonName || '' }
            />
            {
              Array.isArray(countries) && countries?.length === 0
                ? (
                  <StyledTextInputSkeleton theme={ theme }>
                    <div className="cds--form-item">
                      <span className="cds--label cds--skeleton">
                        { t('order.dropShippingData.country') }
                      </span>
                      <div className="cds--skeleton cds--text-input" />
                    </div>
                  </StyledTextInputSkeleton>
                ) : (
                  <NWComboBox
                    key={ order.deliveryAddressData?.country || 'country' }
                    titleText={ t('order.dropShippingData.country') }
                    placeholder={ `${ t('common.select') } ${ t('order.dropShippingData.country') }` }
                    label=""
                    autoComplete="none"
                    disabled={ !countries || !order.deliveryAddressData?.deliveryType || !order.deliveryAddressData?.deliveryMode }
                    light
                    required
                    onFocus={ () => updateDsValidation('country', { isTouched: true }) }
                    invalid={ !dsFormValidation.country.isValid && dsFormValidation.country.isTouched }
                    invalidText={ t('errors.required') }
                    name="countries"
                    id="countries"
                    initialSelectedItem={
                      (countries || []).find(country => country.id === order.deliveryAddressData?.country) || ''
                    }
                    onChange={ item => {
                      updateDsValidation('country', { isValid: !!item.selectedItem });
                      setCountry(item);
                    } }
                    items={ countries || [] }
                  />
                )
            }
            {
              Array.isArray(counties) && counties?.length === 0
                ? (
                  <StyledTextInputSkeleton theme={ theme }>
                    <div className="cds--form-item">
                      <span className="cds--label cds--skeleton">
                        { t('order.dropShippingData.county') }
                      </span>
                      <div className="cds--skeleton cds--text-input" />
                    </div>
                  </StyledTextInputSkeleton>
                ) : (
                  <NWComboBox
                    key={ order.deliveryAddressData?.county || 'county' }
                    titleText={ t('order.dropShippingData.county') }
                    placeholder={ `${ t('common.select') } ${ t('order.dropShippingData.county') }` }
                    label=""
                    disabled={ !counties }
                    light
                    name="counties"
                    id="counties"
                    required
                    onFocus={ () => updateDsValidation('county', { isTouched: true }) }
                    invalid={ !dsFormValidation.county.isValid && dsFormValidation.county.isTouched }
                    invalidText={ t('errors.required') }
                    initialSelectedItem={
                      (counties || []).find(county => county.id === order.deliveryAddressData?.county) || ''
                    }
                    onChange={ item => {
                      updateDsValidation('county', { isValid: !!item.selectedItem });
                      setCounty(item);
                    } }
                    items={ counties || [] }
                  />
                )
            }
            {
              Array.isArray(cities) && cities?.length === 0
                ? (
                  <StyledTextInputSkeleton theme={ theme }>
                    <div className="cds--form-item">
                      <span className="cds--label cds--skeleton">
                        { t('order.dropShippingData.city') }
                      </span>
                      <div className="cds--skeleton cds--text-input" />
                    </div>
                  </StyledTextInputSkeleton>
                ) : (
                  <>
                    <NWComboBox
                      key={ order.deliveryAddressData?.city || 'city' }
                      disabled={ !cities }
                      titleText={ t('order.dropShippingData.city') }
                      placeholder={ `${ t('common.select') } ${ t('order.dropShippingData.city') }` }
                      light
                      label=""
                      id="cities"
                      name="cities"
                      required
                      onFocus={ () => updateDsValidation('city', { isTouched: true }) }
                      invalid={ !dsFormValidation.city.isValid && dsFormValidation.city.isTouched }
                      invalidText={ t('errors.required') }
                      initialSelectedItem={
                        (sortedCities || []).find(city => city.name === order.deliveryAddressData?.city) || ''
                      }
                      hideClearButton={ false }
                      shouldFilterItem={ filterItemsByName() }
                      onChange={ item => {
                        updateDsValidation('city', { isValid: !!item.selectedItem });
                        setCity(item);
                      } }
                      items={ sortedCities || [] }
                    />
                  </>
                )
            }
            { (
              order.deliveryAddressData?.deliveryType !== 'emktp'
              && order.deliveryAddressData?.deliveryMode === 'locker'
            ) && (
              <>
                {
                  Array.isArray(lockers) && lockers?.length === 0
                    ? (
                      <StyledTextInputSkeleton theme={ theme }>
                        <div className="cds--form-item">
                          <span className="cds--label cds--skeleton">
                            { t('order.dropShippingData.locker') }
                          </span>
                          <div className="cds--skeleton cds--text-input" />
                        </div>
                      </StyledTextInputSkeleton>
                    ) : (
                      <NWComboBox
                        titleText={ t('order.dropShippingData.locker') }
                        placeholder={ `${ t('common.select') } ${ t('order.dropShippingData.locker') }` }
                        label=""
                        disabled={ !lockers }
                        light
                        name="lockers"
                        id="lockers"
                        required
                        onFocus={ () => updateDsValidation('locker', { isTouched: true }) }
                        invalid={ !dsFormValidation.locker.isValid && dsFormValidation.locker.isTouched }
                        invalidText={ t('errors.required') }
                        initialSelectedItem={
                          (lockers || []).find(locker => locker.value === order?.deliveryAddressData?.deliveryLockerId) || ''
                        }
                        onChange={ item => {
                          updateDsValidation('locker', { isValid: !!item.selectedItem });
                          setLocker(item);
                        } }
                        items={ lockers || [] }
                      />
                    )
                }
              </>
            ) }
            <NWTextInput
              id="address"
              name="address"
              autoComplete="none"
              required
              light
              readOnly={ order.deliveryAddressData?.deliveryType !== 'emktp' && order.deliveryAddressData?.deliveryMode === 'locker' }
              placeholder={ `${ t('order.address.streetName') }, ${ t('order.address.number') }, ${ t('order.address.building') }, ${ t('order.address.floor') }` }
              labelText={ t('order.dropShippingData.address') }
              onFocus={ () => updateDsValidation('address', { isTouched: true }) }
              invalid={ !dsFormValidation.address.isValid && dsFormValidation.address.isTouched }
              invalidText={ dsFormValidation.address.error }
              onChange={ e => handleOnChangeAddress(e.target.value) }
              value={ order.deliveryAddressData?.address || order.deliveryAddressData?.street || '' }
            />
            <NWTextInput
              id="phone"
              name="phone"
              autoComplete="none"
              labelText={ t('order.dropShippingData.phone') }
              placeholder="07xxxxxxxx"
              required
              light
              onFocus={ () => updateDsValidation('phone', { isTouched: true }) }
              invalid={ !dsFormValidation.phone.isValid && dsFormValidation.phone.isTouched }
              invalidText={ dsFormValidation.phone.error }
              onChange={ e => handleOnChangePhone(e.target.value) }
              value={ order.deliveryAddressData?.contactPersonPhone || '' }
            />
            { (order?.deliveryAddressData?.deliveryType && order?.deliveryAddressData?.deliveryType !== 'emktp') && (
              <NWTextInput
                id="recipientEmail"
                name="recipientEmail"
                autoComplete="none"
                labelText={ t('pages.requestForm.recipientEmail') }
                placeholder="xxxx@xx.xxx"
                required
                light
                onFocus={ () => updateDsValidation('recipientEmail', { isTouched: true }) }
                invalid={ !dsFormValidation.recipientEmail.isValid && dsFormValidation.recipientEmail.isTouched }
                invalidText={ dsFormValidation.recipientEmail.error }
                onChange={ e => handleOnChangeRecipientEmail(e.target.value) }
                value={ order.deliveryAddressData?.contactPersonRecipientEmail || '' }
              />
            ) }
            <NWTextInput
              id="postalCode"
              name="postalCode"
              autoComplete="none"
              labelText={ t('order.dropShippingData.postalCode') }
              light
              readOnly={ order.deliveryAddressData?.deliveryType !== 'emktp' && order.deliveryAddressData?.deliveryMode === 'locker' }
              placeholder={ COUNTRIES[selectedDSCountry]?.zipCodePlaceholder }
              required={ COUNTRIES[selectedDSCountry]?.zipCodeRequired }
              onFocus={ () => updateDsValidation('postalCode', { isTouched: true }) }
              invalid={ !dsFormValidation.postalCode.isValid && dsFormValidation.postalCode.isTouched }
              invalidText={ dsFormValidation.postalCode.error }
              onChange={ e => handleOnChangePostalCode(e.target.value) }
              value={ order.deliveryAddressData?.postalCode || order.deliveryAddressData?.zipCode || order.deliveryAddressData?.zipcode || '' }
            />
            <div className="dsOrderTypeRef">
              <div className="internalRef">
                <NWTextInput
                  id="ref"
                  light
                  autoComplete="none"
                  labelText={ t('order.dropShippingData.internalRef') }
                  required={ order?.deliveryAddressData?.deliveryType === 'emktp' && canSelectedDSCountryPlaceEmagOrder }
                  onFocus={ () => updateDsValidation('internalRefCode', { isTouched: true }) }
                  invalid={ !dsFormValidation.internalRefCode.isValid && dsFormValidation.internalRefCode.isTouched }
                  invalidText={ dsFormValidation.internalRefCode.error }
                  onChange={ e => handleOnChangeInternalRefCode(e.target.value) }
                  value={ order.clientOrderIdRef || '' }
                />
                <Info size={ 18 } id="internalRefTooltip" />
                <ReactTooltip
                  className="internalRefTooltip"
                  anchorId="internalRefTooltip"
                  variant="light"
                  offset={ 10 }
                  content={ (
                    <p
                      id="tooltip-internalRef"
                      dangerouslySetInnerHTML={ {
                        __html: (order?.deliveryAddressData?.deliveryType === 'emktp' && canSelectedDSCountryPlaceEmagOrder)
                          ? t('order.internalRefInfoEmktp')
                          : t('order.internalRefInfo')
                      } }
                    />
                  ) }
                />
              </div>
              { (
                order?.deliveryAddressData?.deliveryType === 'emktp'
                && order?.deliveryAddressData?.deliveryMode === 'locker'
              ) && (
                <div className="deliveryLockerCode">
                  <NWTextInput
                    id="deliveryLockerCode"
                    autoComplete="none"
                    light
                    required
                    invalid={ !dsFormValidation.deliveryLockerCode.isValid && dsFormValidation.deliveryLockerCode.isTouched }
                    invalidText={ dsFormValidation.deliveryLockerCode.error }
                    labelText={ t('order.dropShippingData.deliveryLockerCode') }
                    onFocus={ () => updateDsValidation('deliveryLockerCode', { isTouched: true }) }
                    onChange={ e => handleOnDeliveryLockerCode(e.target.value) }
                    value={ order?.deliveryAddressData?.deliveryLockerCode || '' }
                  />
                  <Info size={ 18 } id="tooltip-deliveryLockerCode" />
                  <ReactTooltip
                    className="tooltip-deliveryLockerCode"
                    anchorId="tooltip-deliveryLockerCode"
                    variant="light"
                    offset={ 10 }
                    content={ (
                      <p
                        id="tooltip-deliveryLockerCode"
                        dangerouslySetInnerHTML={ { __html: t('order.deliveryLockerCodeInfo') } }
                      />
                    ) }
                  />
                </div>
              ) }
            </div>
            <div className="checkboxWrapper">
              <Checkbox
                id={ uniqueId('awb') }
                name="awb"
                data-test="awbPayment"
                labelText={ t('order.dropShippingData.awbPayment') }
                onChange={ (_, { checked }) => order.setCashOnDelivery(checked) }
                checked={ !!order?.cashOnDelivery }
              />
              <div className="tooltipContainer">
                <Info size={ 18 } id="tooltip-awb" />
                <ReactTooltip
                  className="tooltip-awb"
                  anchorId="tooltip-awb"
                  variant="light"
                  offset={ 10 }
                  content={ (
                    <p
                      id="tooltip-awb"
                      dangerouslySetInnerHTML={ { __html: t('order.awbPaymentInfo') } }
                    />
                  ) }
                />
              </div>
            </div>
          </div>
        </section>
        <div>
          <section>
            <div className="titleContainer">
              <span>2</span> <p>{ t('order.shippingMethod') }</p>
            </div>
            <div className="body">
              { loadingOptions ? (
                <NWRadioButtonGroup
                  theme={ theme }
                  name="transportOptions"
                  className="transportOptions"
                  legendText=""
                  onChange={ noop }
                >
                  { Array.from({ length: 3 }, () => {
                    const id = uniqueId();
                    return (
                      <RadioButton
                        key={ id }
                        id={ id }
                        value={ id }
                        labelText={ <TextInputSkeleton hideLabel /> }
                      />
                    );
                  }) }
                </NWRadioButtonGroup>
              ) : (
                <NWRadioButtonGroup
                  theme={ theme }
                  name="transportOptions"
                  className="transportOptions"
                  legendText=""
                  invalid={ !dsFormValidation.deliveryOption.isSelected }
                  invalidText={ t('errors.required') }
                  onChange={ item => {
                    updateDsValidation('deliveryOption', { isSelected: !!item });
                    setTransportOption(item);
                  } }
                  valueSelected={ order.transportOption || '' }
                >
                  { (transportOptions || [])
                    .filter(transportOption => transportOption.code !== 'WAREHOUSE')
                    .map(transportOption => {
                      if (transportOption.code === 'SATURDAY_DELIVERY') {
                        return (
                          <div
                            key={ transportOption.id }
                            className="SATURDAY_DELIVERY cds--radio-button-wrapper"
                          >
                            <input
                              id={ transportOption.id }
                              name="transportOptions"
                              required={ transportOption.isAvailable }
                              type="radio"
                              className="cds--radio-button"
                              value={ transportOption.id }
                              checked={ transportOption.id === order.transportOption }
                              disabled={ !transportOption.isAvailable }
                              onChange={ noop }
                            />
                            <label
                              htmlFor={ transportOption.id }
                              className="cds--radio-button__label"
                            >
                              <span className="cds--radio-button__appearance" />
                              <span className="" dir="auto">{ transportOption.name }</span>
                              { !transportOption.isAvailable && (
                                <>
                                  <Info size={ 18 } id={ `${ transportOption.id }_delivery }` } />
                                  <ReactTooltip
                                    className={ `${ transportOption.id }_delivery }` }
                                    anchorId={ `${ transportOption.id }_delivery }` }
                                    variant="light"
                                    offset={ 10 }
                                    content={ (
                                      <p id={ `${ transportOption.id }_delivery` }>
                                        { t('order.delivery.saturdayDeliveryInfo') }
                                      </p>
                                    ) }
                                  />
                                </>
                              ) }
                            </label>
                          </div>
                        );
                      }
                      return (
                        <RadioButton
                          className={ transportOption.code }
                          key={ transportOption.id }
                          id={ transportOption.id }
                          // disabled={ !transportOption.isAvailable }
                          name={ transportOption.code }
                          labelText={ transportOption.name }
                          value={ transportOption.id }
                          required
                        />
                      );
                    }) }
                </NWRadioButtonGroup>
              ) }
            </div>
          </section>
          <section>
            <div className="titleContainer">
              <span>3</span> <p>{ t('order.billingDetails') }</p>
            </div>
            <div className="body">
              {
                loadingAddresses
                  ? <TextInputSkeleton className="addressesSkeleton" />
                  : (
                    <NWComboBox
                      required
                      key={ selectedBillingAddress?.id || 'billingAddresses' }
                      id="billingAddresses"
                      name="billingAddresses"
                      titleText={ t('order.selectBillingAddress') }
                      onFocus={ () => updateDsValidation('billingAddress', { isTouched: true }) }
                      invalid={ !dsFormValidation.billingAddress.isValid && dsFormValidation.billingAddress.isTouched }
                      invalidText={ t('errors.required') }
                      label=""
                      onChange={ item => {
                        updateDsValidation('billingAddress', { isValid: !!item.selectedItem });
                        setBilling(item);
                      } }
                      items={ billingAddresses }
                      initialSelectedItem={
                        selectedBillingAddress || billingAddresses.find(addr => addr.isDefault) || ''
                      }
                    />
                  )
              }
              <NWTextInput
                id="billingDetails"
                light
                autoComplete="none"
                labelText={ t('order.dropShippingData.billingDetailsInfo') }
                onFocus={ () => updateDsValidation('billingDetails', { isTouched: true }) }
                invalid={ !dsFormValidation.billingDetails.isValid && dsFormValidation.billingDetails.isTouched }
                invalidText={ dsFormValidation.billingDetails.error }
                placeholder={ t('order.billingDetailsInfoPlaceholder') }
                onChange={ e => handleOnChangeBillingDetails({ dropShipment: true, value: e.target.value }) }
                value={ order.billingDetails || '' }
              />
            </div>
          </section>
          <section>
            <div className="titleContainer">
              <span>4</span> <p>{ t('order.paymentMethod') }</p>
            </div>
            <div className="body">
              <NWRadioButtonGroup
                name="paymentOptions"
                theme={ theme }
                legendText=""
                invalid={ !dsFormValidation.paymentOption.isSelected }
                invalidText={ t('errors.required') }
                onChange={ item => {
                  updateDsValidation('paymentOption', { isSelected: !!item });
                  setPaymentOption(item);
                } }
                valueSelected={ order.paymentOption || '' }
                disabled={ loadingOptions }
              >
                { loadingOptions
                  ? Array.from({ length: 3 }, () => {
                    const id = uniqueId();
                    return (
                      <RadioButton
                        key={ id }
                        id={ id }
                        value={ id }
                        labelText={ <TextInputSkeleton hideLabel /> }
                      />
                    );
                  })
                  : (paymentOptions || []).map(paymentOption => (
                    <RadioButton
                      key={ paymentOption.id }
                      id={ paymentOption.id }
                      disabled={ paymentOption.disabled }
                      labelText={ paymentOption.name }
                      value={ paymentOption.id }
                      name={ paymentOption.code }
                      required={ !paymentOption.disabled }
                    />
                  ))
                }
              </NWRadioButtonGroup>
            </div>
          </section>
          <section>
            <TextArea
              className="orderNotes"
              labelText=""
              name="orderNotes"
              id="orderNotes"
              cols={ 30 }
              rows={ 5 }
              placeholder={ t('order.observations') }
              onChange={ onChangeNote }
              value={ order.orderNotes || order.comment || '' }
            />
          </section>
        </div>
      </>
    );
  }, { isEqual: dequal });

  const getMemoizedBlocks = memoize(data => {
    const {
      order,
      editable,
      transportOptions,
      paymentOptions,
      billingAddresses,
      shippingAddresses,
      loadingBlocks,
      staticBlocks,
      selectedBillingAddress,
      selectedShippingAddress,
      formValidation,
      updateValidation,
      handleOnChangeBillingDetails,
    } = data;

    if (!editable) {
      return (
        <>
          <div>
            <section>
              <div className="titleContainer">
                <span>1</span> <p>{ t('order.deliveryMethod') }</p>
              </div>
              <div className="body">
                { loadingAddresses
                  ? <TextInputSkeleton className="addressesSkeleton" />
                  : (
                    <p>
                      <span>{ t('orders.deliveryAddress') }:</span>
                      { order.hasDeliveryAddress ? selectedShippingAddress?.label : t('order.unavailableAddress') }
                    </p>
                  )
                }
                { loadingOptions
                  ? <TextInputSkeleton />
                  : (
                    <p>
                      <span>{ t('orders.deliveryType') }:</span>
                      { (transportOptions || []).find(transportOption => transportOption.id === order.transportOption)?.name }
                    </p>
                  )
                }
              </div>
            </section>
          </div>
          <div>
            <section>
              <div className="titleContainer">
                <span>2</span> <p>{ t('order.billingData') }</p>
              </div>
              <div className="body">
                { loadingAddresses
                  ? <TextInputSkeleton className="addressesSkeleton" />
                  : (
                    <>
                      <p>
                        <span>{ t('order.billingAddress') }:</span>
                        { selectedBillingAddress?.label }
                      </p>
                    </>
                  )
                }
                { order.billingDetails && (
                  <p>
                    <span>{ t('order.billingDetails') }:</span>
                    { order.billingDetails }
                  </p>
                ) }
              </div>
            </section>
            <section>
              <div className="titleContainer">
                <span>3</span> <p>{ t('order.paymentMethod') }</p>
              </div>
              <div className="body">
                { loadingOptions
                  ? <TextInputSkeleton />
                  : (
                    <p>
                      { (paymentOptions || []).find(opt => opt.id === order.paymentOption)?.name }
                    </p>
                  )
                }
              </div>
            </section>
            { (order.orderNotes?.length > 0 || order.comment?.length > 0) && (
              <section className="orderNotes">
                <div className="body">
                  <p>{ order.orderNotes || order.comment }</p>
                </div>
              </section>
            ) }
          </div>
        </>
      );
    }

    const hour = (new Date()).getHours();

    return (
      <>
        <div>
          <section>
            <div className="titleContainer">
              <span>1</span> <p>{ t('order.deliveryMethod') }</p>
            </div>
            <div className="body">
              {
                loadingAddresses
                  ? <TextInputSkeleton className="addressesSkeleton" />
                  : (
                    <NWComboBox
                      required
                      key={ `shipping-${shippingAddresses.length}-${selectedShippingAddress?.id || 'none'}` }
                      id="shippingAddresses"
                      name="shippingAddresses"
                      titleText={ t('order.selectDeliveryAddress') }
                      onFocus={ () => updateValidation('deliveryAddress', { isTouched: true }) }
                      invalid={ !formValidation.deliveryAddress.isValid && formValidation.deliveryAddress.isTouched && !selectedShippingAddress }
                      invalidText={ t('errors.required') }
                      label=""
                      itemToString={ adr => adr?.fullAddress }
                      onChange={ item => {
                        updateValidation('deliveryAddress', { isValid: !!item.selectedItem });
                        setShipping(item);
                      } }
                      items={ shippingAddresses }
                      light
                      initialSelectedItem={
                        selectedShippingAddress || shippingAddresses.find(addr => addr.isDefault) || null
                      }
                    />
                  )
              }
              <div className="options">
                { loadingOptions ? (
                  <NWRadioButtonGroup
                    theme={ theme }
                    name="transportOptions"
                    className="transportOptions"
                    legendText=""
                    onChange={ noop }
                  >
                    { Array.from({ length: 3 }, () => {
                      const id = uniqueId();
                      return (
                        <RadioButton
                          key={ id }
                          id={ id }
                          value={ id }
                          labelText={ <TextInputSkeleton hideLabel /> }
                        />
                      );
                    }) }
                  </NWRadioButtonGroup>
                ) : (
                  <NWRadioButtonGroup
                    theme={ theme }
                    name="transportOptions"
                    className="transportOptions"
                    legendText=""
                    invalid={ !formValidation.deliveryOption.isSelected }
                    invalidText={ t('errors.required') }
                    onChange={ item => {
                      updateValidation('deliveryOption', { isSelected: !!item });
                      setTransportOption(item);
                    } }
                    disabled={ loadingOptions }
                    valueSelected={ order.transportOption || '' }
                  >
                    { (transportOptions || []).map(transportOption => {
                      if (transportOption.code === 'SATURDAY_DELIVERY') {
                        return (
                          <div
                            key={ transportOption.id }
                            className="SATURDAY_DELIVERY cds--radio-button-wrapper"
                          >
                            <input
                              id={ transportOption.id }
                              name="transportOptions"
                              required={ transportOption.isAvailable }
                              type="radio"
                              className="cds--radio-button"
                              value={ transportOption.id }
                              checked={ transportOption.id === order.transportOption }
                              disabled={ !transportOption.isAvailable }
                              onChange={ noop }
                            />
                            <label
                              htmlFor={ transportOption.id }
                              className="cds--radio-button__label"
                            >
                              <span className="cds--radio-button__appearance" />
                              <span className="" dir="auto">{ transportOption.name }</span>
                              { !transportOption.isAvailable && (
                                <>
                                  <Info size={ 18 } id={ `${ transportOption.id }_delivery` } />
                                  <ReactTooltip
                                    className={ `${ transportOption.id }_delivery` }
                                    anchorId={ `${ transportOption.id }_delivery` }
                                    variant="light"
                                    offset={ 10 }
                                    content={ (
                                      <p id={ `${ transportOption.id }_delivery` }>
                                        { t('order.delivery.saturdayDeliveryInfo') }
                                      </p>
                                    ) }
                                  />
                                </>
                              ) }
                            </label>
                          </div>
                        );
                      }
                      return (
                        <RadioButton
                          className={ transportOption.code }
                          key={ transportOption.id }
                          required={ transportOption.isAvailable !== false }
                          disabled={ transportOption.isAvailable === false }
                          id={ transportOption.id }
                          name={ transportOption.code }
                          labelText={ transportOption.name }
                          value={ transportOption.id }
                        />
                      );
                    }) }
                  </NWRadioButtonGroup>
                )}
                { !isExternalReseller && (
                  <div className="deliveryConditions">
                    <p className="title">{ t('order.deliveryConditions') }</p>
                    { loadingBlocks && <SkeletonText paragraph lineCount={ 4 } /> }
                    { (!loadingBlocks && Object.keys(staticBlocks).length > 0) && (
                      <>
                        { (order?.transportOption === transportOptions?.find(opt => opt.code === 'WAREHOUSE')?.id) && (
                          <div
                            dangerouslySetInnerHTML={ { __html: staticBlocks.warehouseOptionMessage } }
                          />
                        ) }
                        { (
                          transportOptions
                            .filter(opt => opt.code === 'COURIER' || opt.code === 'SATURDAY_DELIVERY')
                            .map(opt => opt.id)
                            .includes(order.transportOption)
                        )
                          ? (
                            <>
                              <div
                                dangerouslySetInnerHTML={ { __html: staticBlocks.deliveryOptionMessage } }
                              />
                              { staticBlocks.valueLimit && (
                                <div
                                  dangerouslySetInnerHTML={ { __html: staticBlocks.valueLimit } }
                                />
                              ) }
                            </>
                          )
                          : hour < 16 && (
                            <p className="warning">
                              { t('orders.deliveryWarningMessage') }
                            </p>
                          ) }
                      </>
                    ) }
                    { !loadingBlocks && Object.keys(staticBlocks).length === 0 && hour < 16 && (
                      <p className="warning">
                        { t('orders.deliveryWarningMessage') }
                      </p>
                    ) }
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
        <div>
          <section>
            <div className="titleContainer">
              <span>2</span> <p>{ t('order.billingData') }</p>
            </div>
            <div className="body">
              {
                loadingAddresses
                  ? <TextInputSkeleton className="addressesSkeleton" />
                  : (
                    <>
                      <NWComboBox
                        required
                        key={ `billing-${billingAddresses.length}-${selectedBillingAddress?.id || 'none'}` }
                        id="billingAddresses"
                        name="billingAddresses"
                        titleText={ t('order.selectBillingAddress') }
                        onFocus={ () => updateValidation('billingAddress', { isTouched: true }) }
                        invalid={ !formValidation.billingAddress.isValid && formValidation.billingAddress.isTouched && !selectedBillingAddress }
                        invalidText={ t('errors.required') }
                        label=""
                        initialSelectedItem={
                          selectedBillingAddress || billingAddresses.find(addr => addr.isDefault) || null
                        }
                        onChange={ item => {
                          updateValidation('billingAddress', { isValid: !!item.selectedItem });
                          setBilling(item);
                        } }
                        items={ billingAddresses }
                      />
                      <NWTextInput
                        id="billingDetails"
                        light
                        autoComplete="none"
                        labelText={ t('order.dropShippingData.billingDetailsInfo') }
                        invalid={ !formValidation.billingDetails.isValid }
                        invalidText={ formValidation.billingDetails.error }
                        placeholder={ t('order.billingDetailsInfoPlaceholder') }
                        onChange={ e => handleOnChangeBillingDetails({ dropShipment: false, value: e.target.value }) }
                        value={ order.billingDetails || '' }
                      />
                    </>
                  )
              }
            </div>
          </section>
          <section>
            <div className="titleContainer">
              <span>3</span> <p>{ t('order.paymentMethod') }</p>
            </div>
            <div className="body">
              <NWRadioButtonGroup
                theme={ theme }
                className="paymentOptions"
                name="paymentOptions"
                legendText=""
                invalid={ !formValidation.paymentOption.isSelected }
                invalidText={ t('errors.required') }
                onChange={ item => {
                  updateValidation('paymentOption', { isSelected: !!item });
                  setPaymentOption(item);
                } }
                valueSelected={ order.paymentOption || '' }
                disabled={ loadingOptions }
              >
                { loadingOptions
                  ? Array.from({ length: 2 }, () => {
                    const id = uniqueId();
                    return (
                      <RadioButton
                        key={ id }
                        id={ id }
                        value={ id }
                        labelText={ <TextInputSkeleton hideLabel /> }
                      />
                    );
                  })
                  : (paymentOptions || []).map(paymentOpt => (
                    <RadioButton
                      required
                      key={ paymentOpt.id }
                      id={ paymentOpt.id }
                      labelText={ paymentOpt.name }
                      value={ paymentOpt.id }
                      name={ paymentOpt.code }
                    />
                  )) }
              </NWRadioButtonGroup>
            </div>
          </section>
          <section>
            <TextArea
              className="orderNotes"
              labelText=""
              name="orderNotes"
              id="orderNotes"
              cols={ 30 }
              rows={ 5 }
              placeholder={ `${ t('order.observations') }...` }
              onChange={ onChangeNote }
              value={ order.orderNotes || order.comment || '' }
            />
          </section>
        </div>
      </>
    );
  }, { isEqual: dequal });

  return (
    <StyledContainer theme={ theme }>
      { dropShipment
        ? getMemoizedDropShipmentBlocks(props)
        : getMemoizedBlocks(props)
      }
    </StyledContainer>
  );
};

/* eslint-disable react/no-unused-prop-types */
OrderInfo.propTypes = {
  counties: array,
  cities: array,
  editable: bool,
  transportOptions: array,
  paymentOptions: array,
  setCountry: func,
  setCounty: func,
  setCity: func,
  setLocker: func,
  staticBlocks: object,
  canPlaceEmagOrder: bool,
  canPlaceExternalOrder: bool,
  canPlaceInternalOrder: bool,
  selectedBillingAddress: object,
  selectedShippingAddress: object,
  setPaymentOption: func,
  setTransportOption: func,
  order: object,
  setShipping: func,
  setBilling: func,
  onChangeNote: func,
  loadingBlocks: bool,
  lockers: array,
  loadingOptions: bool,
  loadingAddresses: bool,
  selectedDSCountry: number,
  canSelectedDSCountryPlaceEmagOrder: bool,
  hasInternalDropShipmentSamedayLockerTransport: bool,
  hasExternalDropShipmentSamedayLockerTransport: bool,
  formValidation: object,
  updateValidation: func,
  dsFormValidation: object,
  updateDsValidation: func,
  handleOnChangeName: func,
  handleOnChangeAddress: func,
  handleOnChangePhone: func,
  handleOnChangePostalCode: func,
  handleOnChangeInternalRefCode: func,
  handleOnDeliveryLockerCode: func,
  handleOnChangeDeliveryType: func,
  handleOnChangeDeliveryMode: func,
  handleOnChangeRecipientEmail: func,
  handleOnChangeBillingDetails: func,
  mappedDeliveryTypes: object,
};

OrderInfo.displayName = 'OrderInfoComponent';

export default OrderInfo;
